import api from '@/lib/api';
import type { PageResponse } from '@/types/admin.model';
import { toPageResponse } from '@/types/admin.model';

export type AdminAuditAction =
  | 'BAN_USER'
  | 'UNBAN_USER'
  | 'GRANT_OWNER_ROLE'
  | 'REVOKE_OWNER_ROLE'
  | 'ENABLE_CLUB'
  | 'UPDATE_APP_VERSION'
  | 'CREATE_EMAIL_TEMPLATE'
  | 'DELETE_EMAIL_TEMPLATE'
  | 'RUN_JOB'
  | 'IMPERSONATE_START'
  | 'IMPERSONATE_TAKEOVER'
  | 'APPROVE_DEBT_PAYMENT'
  | 'REJECT_DEBT_PAYMENT';

export interface AdminAuditLog {
  id: string;
  adminEmail: string;
  adminName: string;
  action: AdminAuditAction;
  targetType: string | null;
  targetId: string | null;
  payload: string | null;
  result: 'OK' | 'ERROR';
  errorMessage: string | null;
  createdAt: string;
}

interface SearchParams {
  adminEmail?: string;
  action?: AdminAuditAction;
  /** Exact match on the audited row's target — a user pk, a club id, a job path. */
  targetId?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

export const ADMIN_AUDIT_ACTIONS: AdminAuditAction[] = [
  'BAN_USER',
  'UNBAN_USER',
  'GRANT_OWNER_ROLE',
  'REVOKE_OWNER_ROLE',
  'ENABLE_CLUB',
  'UPDATE_APP_VERSION',
  'CREATE_EMAIL_TEMPLATE',
  'DELETE_EMAIL_TEMPLATE',
  'RUN_JOB',
  // The impersonation pair shipped with phase 2 and never made it into this list, so the support
  // session was the one thing the audit screen could not filter by. The debt pair is phase 3.
  'IMPERSONATE_START',
  'IMPERSONATE_TAKEOVER',
  'APPROVE_DEBT_PAYMENT',
  'REJECT_DEBT_PAYMENT',
];

export const adminAuditApi = {
  search: async ({
    adminEmail,
    action,
    targetId,
    from,
    to,
    page = 0,
    size = 20,
  }: SearchParams): Promise<PageResponse<AdminAuditLog>> => {
    const response = await api.get('/admin/audit', {
      params: {
        adminEmail: adminEmail || undefined,
        action: action || undefined,
        targetId: targetId || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        size,
      },
    });
    return toPageResponse<AdminAuditLog>(response.data);
  },
};
